import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'motion/react'
import { AlertTriangle, ArrowUpRight, Check, Copy, Lock, Search, Trash2, Upload as UploadIcon } from 'lucide-react'
import { queryKeys, useDeleteUpload, useLibrary, useMeta, useUploads } from '@/api/queries'
import type { LibraryItem, LibraryWebsite, Upload } from '@/api/types'
import { cn } from '@/lib/cn'
import { riseIn, spring, stagger } from '@/lib/motion'
import { formatBytes } from '@/lib/upload'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useUiStore } from '@/stores/uiStore'
import { FileBadge } from '@/components/documents/FileBadge'
import { UploadDialog } from '@/components/documents/UploadDialog'
import { TopBar } from '@/components/layout/TopBar'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/Dialog'
import { IconButton } from '@/components/ui/IconButton'
import { Notice } from '@/components/ui/Notice'
import { RelativeTime } from '@/components/ui/RelativeTime'
import { Segmented } from '@/components/ui/Segmented'
import { Skeleton } from '@/components/ui/Skeleton'
import { Spinner } from '@/components/ui/Spinner'

export function DocumentsPage() {
  const meta = useMeta()
  const { id } = useParams()
  const openViewer = useUiStore((s) => s.openViewer)
  const closeViewer = useUiStore((s) => s.closeViewer)
  const setScrolled = useUiStore((s) => s.setContentScrolled)
  const [search, setSearch] = useState('')
  const [department, setDepartment] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const q = useDebouncedValue(search.trim(), 250)
  const library = useLibrary(q, department)
  const canUpload = Boolean(meta.user?.can_upload)

  useEffect(() => {
    document.title = `Documents · ${meta.assistant_name}`
  }, [meta.assistant_name])

  // /documents/:id opens the viewer; leaving that URL (Back, close) closes it.
  useEffect(() => {
    const docId = Number(id)
    if (!id || !Number.isInteger(docId)) return
    openViewer(docId)
    return () => closeViewer()
  }, [id, openViewer, closeViewer])

  useEffect(() => () => setScrolled(false), [setScrolled])

  return (
    <main id="main" className="flex min-w-0 flex-1 flex-col" aria-label="Documents">
      <TopBar title="Documents">
        {canUpload && (
          <Button size="sm" onClick={() => setUploading(true)}>
            <UploadIcon className="size-3.5" aria-hidden />
            Upload
          </Button>
        )}
      </TopBar>
      <div className="min-h-0 flex-1 overflow-y-auto" onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 4)}>
        <div className="mx-auto w-full max-w-6xl px-4 pb-20 sm:px-6">
          <motion.header variants={stagger(0.06)} initial="hidden" animate="show" className="pb-6 pt-8 sm:pt-12">
            <motion.h1
              variants={riseIn}
              className="text-[34px] font-semibold leading-tight tracking-display text-ink sm:text-[40px]"
            >
              Library
            </motion.h1>
            <motion.p variants={riseIn} className="mt-2 max-w-xl text-[15px] text-ink-muted">
              Every document the assistant answers from. Open one to read it right here
              {meta.user?.can_download ? '.' : ' — originals stay in the app.'}
            </motion.p>
            <motion.div variants={riseIn} className="relative mt-6 max-w-2xl">
              <Search
                className="pointer-events-none absolute left-4 top-1/2 size-[18px] -translate-y-1/2 text-ink-faint"
                aria-hidden
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search titles and summaries"
                aria-label="Search documents"
                className="h-12 w-full rounded-2xl bg-surface pl-11 pr-4 text-[15px] text-ink shadow-card transition-shadow duration-300 placeholder:text-ink-faint focus:shadow-[0_0_0_3px_rgb(var(--accent)/0.25),0_8px_24px_-12px_rgb(0_0_0/0.2)] focus:outline-none"
              />
            </motion.div>
            {meta.departments.length > 0 && (
              <motion.div
                variants={riseIn}
                className="mt-4 flex flex-wrap gap-1.5"
                role="radiogroup"
                aria-label="Department"
              >
                <DepartmentChip label="All" active={department === null} onClick={() => setDepartment(null)} />
                {meta.departments.map((d) => (
                  <DepartmentChip
                    key={d.name}
                    label={d.name}
                    count={d.documents}
                    active={department === d.name}
                    onClick={() => setDepartment(department === d.name ? null : d.name)}
                  />
                ))}
              </motion.div>
            )}
          </motion.header>

          {canUpload && <UploadsSection onUpload={() => setUploading(true)} />}

          <section aria-labelledby="library-heading" className="mt-2">
            <div className="mb-3 flex items-baseline justify-between">
              <h2 id="library-heading" className="text-[13px] font-semibold uppercase tracking-wider text-ink-faint">
                {q || department ? 'Results' : 'All documents'}
              </h2>
              {library.data && (
                <span className="text-xs tabular-nums text-ink-faint" aria-live="polite">
                  {library.data.total.toLocaleString()} {library.data.total === 1 ? 'document' : 'documents'}
                  {library.data.websites?.length > 0 &&
                    ` · ${library.data.websites.length} ${library.data.websites.length === 1 ? 'website' : 'websites'}`}
                </span>
              )}
            </div>
            {library.isPending ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }, (_, i) => (
                  <Skeleton key={i} className="h-40 rounded-2xl" />
                ))}
              </div>
            ) : library.error ? (
              <Notice tone="danger" title="Couldn’t load documents">
                Try again in a moment.
              </Notice>
            ) : library.data.items.length === 0 && !library.data.websites?.length ? (
              <div className="rounded-2xl bg-surface p-10 text-center shadow-card">
                <p className="font-medium text-ink">
                  {q || department ? 'Nothing matches that search' : 'No documents yet'}
                </p>
                <p className="mt-1 text-sm text-ink-muted">
                  {q || department
                    ? 'Try fewer words, or another department.'
                    : canUpload
                      ? 'Upload the first one to get started.'
                      : 'Documents appear here once an administrator adds them.'}
                </p>
              </div>
            ) : (
              <>
                {library.data.websites?.length > 0 && (
                  <WebsitesList websites={library.data.websites} searching={Boolean(q || department)} />
                )}
                <motion.ul
                  key={`${q}|${department ?? ''}`}
                  variants={stagger(0.035)}
                  initial="hidden"
                  animate="show"
                  className={cn(
                    'grid gap-3 sm:grid-cols-2 lg:grid-cols-3',
                    library.isPlaceholderData && 'opacity-60 transition-opacity',
                  )}
                >
                  {library.data.items.map((item) => (
                    <LibraryCard key={item.id} item={item} />
                  ))}
                </motion.ul>
              </>
            )}
          </section>
        </div>
      </div>
      <UploadDialog open={uploading} onOpenChange={setUploading} />
    </main>
  )
}

function DepartmentChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count?: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={cn(
        'pressable relative h-8 rounded-full px-3.5 text-[13px] font-medium',
        active ? 'text-accent-ink' : 'bg-ink/[0.05] text-ink-muted hover:text-ink dark:bg-white/[0.06]',
      )}
    >
      {active && (
        <motion.span
          layoutId="library-department"
          transition={spring.snappy}
          className="absolute inset-0 rounded-full bg-accent shadow-[0_2px_8px_-2px_rgb(var(--accent)/0.5)]"
          aria-hidden
        />
      )}
      <span className="relative">
        {label}
        {count !== undefined && (
          <span className={cn('ml-1.5 tabular-nums', active ? 'opacity-80' : 'text-ink-faint')}>{count}</span>
        )}
      </span>
    </button>
  )
}

function LibraryCard({ item }: { item: LibraryItem }) {
  const navigate = useNavigate()
  return (
    <motion.li variants={riseIn}>
      <button
        type="button"
        onClick={() => navigate(`/documents/${item.id}`, { state: { fromLibrary: true } })}
        className="group flex h-full w-full flex-col rounded-2xl bg-surface p-4 text-left shadow-card transition-[box-shadow,transform] duration-300 ease-out hover:-translate-y-0.5 hover:shadow-lift active:scale-[0.985] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        <div className="flex items-start gap-3">
          <FileBadge fileType={item.file_type} />
          <h3 className="line-clamp-2 min-w-0 flex-1 text-[14.5px] font-semibold leading-5 text-ink">{item.title}</h3>
        </div>
        {item.summary && <p className="mt-3 line-clamp-3 text-[13px] leading-5 text-ink-muted">{item.summary}</p>}
        <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-4 text-xs text-ink-faint">
          {item.department && <Badge tone="accent">{item.department}</Badge>}
          <span className="truncate">{item.source_name}</span>
          <span aria-hidden>·</span>
          <RelativeTime value={item.updated_at} />
        </div>
      </button>
    </motion.li>
  )
}

function hostName(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

/** Crawled websites, one card each — listing every page would crowd out the files. */
function WebsitesList({ websites, searching }: { websites: LibraryWebsite[]; searching: boolean }) {
  return (
    <motion.ul
      variants={stagger(0.035)}
      initial="hidden"
      animate="show"
      className="mb-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
      aria-label="Websites"
    >
      {websites.map((site) => {
        const pages = `${site.pages.toLocaleString()} ${searching ? 'matching ' : ''}${site.pages === 1 ? 'page' : 'pages'}`
        const body = (
          <>
            <div className="flex items-start gap-3">
              <FileBadge fileType="web page" />
              <div className="min-w-0 flex-1">
                <h3 className="line-clamp-2 text-[14.5px] font-semibold leading-5 text-ink">{site.name}</h3>
                {site.url && <p className="truncate text-[13px] text-ink-muted">{hostName(site.url)}</p>}
              </div>
              {site.url && (
                <ArrowUpRight
                  className="size-4 shrink-0 text-ink-faint transition-colors group-hover:text-accent-text"
                  aria-hidden
                />
              )}
            </div>
            <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-4 text-xs text-ink-faint">
              {site.department && <Badge tone="accent">{site.department}</Badge>}
              <span className="tabular-nums">{pages}</span>
              <span aria-hidden>·</span>
              <RelativeTime value={site.updated_at} />
            </div>
          </>
        )
        const card = 'flex h-full w-full flex-col rounded-2xl bg-surface p-4 text-left shadow-card'
        return (
          <motion.li key={site.source_id} variants={riseIn}>
            {site.url ? (
              <a
                href={site.url}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  card,
                  'group transition-[box-shadow,transform] duration-300 ease-out hover:-translate-y-0.5 hover:shadow-lift active:scale-[0.985] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
                )}
              >
                {body}
                <span className="sr-only"> (opens the website in a new tab)</span>
              </a>
            ) : (
              <div className={card}>{body}</div>
            )}
          </motion.li>
        )
      })}
    </motion.ul>
  )
}

const STATUS: Record<Upload['status'], { label: string; tone: 'neutral' | 'success' | 'danger' | 'warning' }> = {
  processing: { label: 'Indexing', tone: 'neutral' },
  ready: { label: 'Ready', tone: 'success' },
  error: { label: 'Failed', tone: 'danger' },
  duplicate: { label: 'Already in library', tone: 'warning' },
}

function UploadsSection({ onUpload }: { onUpload: () => void }) {
  const meta = useMeta()
  const client = useQueryClient()
  const isAdmin = Boolean(meta.user?.is_admin)
  const [scope, setScope] = useState<'mine' | 'all'>('mine')
  const uploads = useUploads(scope, true)
  const processing = uploads.data?.filter((u) => u.status === 'processing').length ?? 0
  const wasProcessing = useRef(processing)

  // Newly indexed uploads should show up in the library below (and in the department counts).
  useEffect(() => {
    if (processing < wasProcessing.current) {
      void client.invalidateQueries({ queryKey: ['library'] })
      void client.invalidateQueries({ queryKey: queryKeys.meta })
    }
    wasProcessing.current = processing
  }, [processing, client])

  if (!uploads.data?.length && scope === 'mine' && !isAdmin) {
    return (
      <motion.button
        type="button"
        onClick={onUpload}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 flex w-full items-center gap-4 rounded-2xl border-[1.5px] border-dashed border-line-strong px-5 py-4 text-left transition-colors hover:bg-ink/[0.03]"
      >
        <span className="flex size-10 items-center justify-center rounded-full bg-accent-soft text-accent-text">
          <UploadIcon className="size-[18px]" aria-hidden />
        </span>
        <span>
          <span className="block text-[14px] font-medium text-ink">Add a document</span>
          <span className="block text-[13px] text-ink-muted">You can upload files for everyone to ask about.</span>
        </span>
      </motion.button>
    )
  }

  return (
    <section aria-labelledby="uploads-heading" className="mb-10">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 id="uploads-heading" className="text-[13px] font-semibold uppercase tracking-wider text-ink-faint">
          {scope === 'mine' ? 'Your uploads' : 'All uploads'}
        </h2>
        {isAdmin && (
          <Segmented
            label="Show uploads from"
            size="sm"
            value={scope}
            onChange={setScope}
            options={[
              { value: 'mine', label: 'Mine' },
              { value: 'all', label: 'Everyone' },
            ]}
          />
        )}
      </div>
      {uploads.isPending ? (
        <Skeleton className="h-16 rounded-2xl" />
      ) : !uploads.data?.length ? (
        <p className="rounded-2xl bg-surface px-5 py-4 text-sm text-ink-muted shadow-card">
          No uploads yet.{' '}
          <button type="button" onClick={onUpload} className="font-medium text-accent-text hover:underline">
            Upload a document
          </button>
        </p>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-2xl bg-surface shadow-card">
          <AnimatePresence initial={false}>
            {uploads.data.map((upload) => (
              <UploadRow key={upload.id} upload={upload} showOwner={scope === 'all'} />
            ))}
          </AnimatePresence>
        </ul>
      )}
    </section>
  )
}

function UploadRow({ upload, showOwner }: { upload: Upload; showOwner: boolean }) {
  const navigate = useNavigate()
  const remove = useDeleteUpload()
  const [confirming, setConfirming] = useState(false)
  const status = STATUS[upload.status]
  const openable = upload.status === 'ready' && upload.document_id !== null

  return (
    <motion.li
      layout
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={spring.smooth}
      className="overflow-hidden"
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <FileBadge fileType={upload.filename.split('.').pop()} size="sm" />
        <div className="min-w-0 flex-1">
          {openable ? (
            <button
              type="button"
              onClick={() =>
                navigate(`/documents/${upload.document_id}`, {
                  state: { fromLibrary: true },
                })
              }
              className="block max-w-full truncate text-left text-[14px] font-medium text-ink hover:text-accent-text"
            >
              {upload.title}
            </button>
          ) : (
            <p className="truncate text-[14px] font-medium text-ink">{upload.title}</p>
          )}
          <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-ink-faint">
            <span>{formatBytes(upload.size)}</span>
            <span aria-hidden>·</span>
            <RelativeTime value={upload.uploaded_at} />
            {upload.department && (
              <>
                <span aria-hidden>·</span>
                <span>{upload.department}</span>
              </>
            )}
            {showOwner && (
              <>
                <span aria-hidden>·</span>
                <span>{upload.uploaded_by}</span>
              </>
            )}
            {upload.visibility === 'admins' && (
              <span className="inline-flex items-center gap-1 text-warning">
                <Lock className="size-3" aria-hidden /> Admins only
              </span>
            )}
          </p>
          {upload.error && (
            <p className="mt-1 flex items-start gap-1.5 text-xs text-danger">
              <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden />
              {upload.error}
            </p>
          )}
        </div>
        <Badge tone={status.tone} size="md" className="shrink-0">
          {upload.status === 'processing' ? (
            <Spinner className="size-3" />
          ) : upload.status === 'ready' ? (
            <Check className="size-3" aria-hidden />
          ) : upload.status === 'duplicate' ? (
            <Copy className="size-3" aria-hidden />
          ) : null}
          {status.label}
        </Badge>
        <IconButton label="Remove upload" size="sm" onClick={() => setConfirming(true)} disabled={remove.isPending}>
          <Trash2 />
        </IconButton>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Remove this document?"
        description={
          <>
            <strong className="font-medium text-ink">{upload.title}</strong> will be removed from the library and the
            assistant will stop using it in answers.
          </>
        }
        confirmLabel="Remove"
        tone="danger"
        onConfirm={() => remove.mutateAsync(upload.id)}
      />
    </motion.li>
  )
}
